import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'Tooltip';
import { Dropdown, type SingleSectionItems, type SectionedItems } from 'Dropdown';
import { useClickAway } from 'react-use';
import { AudioControlBarButton } from './AudioControlBarButton';
import { getDropdownItemsForDevices, getInputDeviceKeyForDeviceId, getOutputDeviceKeyForDeviceId } from '../utils';

type Props = {
    activeInputDeviceId: string;
    activeOutputDeviceId: string;
    onSelect: (deviceIds: string[]) => void;
    inputDevices: MediaDeviceInfo[];
    outputDevices: MediaDeviceInfo[];
};

// This intentionally uses the shared `Dropdown` directly rather than the `Select` component.
// `Select` is a single-value picker whose trigger shows the selected option's label + a chevron;
// this device picker doesn't fit that shape: its trigger is a gear-icon button that shows no
// selected value, it's wrapped in a Tooltip with controlled open state + click-away handling and
// `placement="top"`, it renders a two-section (input/output) menu with two simultaneously-selected
// keys, uses `isReadOnly` items, and emits the raw selected-keys array. See https://trello.com/c/Xc2QdAJp.
// oxlint-disable-next-line max-lines-per-function
export function AudioSelectButton({
    activeInputDeviceId,
    activeOutputDeviceId,
    onSelect,
    inputDevices,
    outputDevices,
}: Props) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotVoiceAgent.audioControlBar' });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownItems, setDropdownItems] = useState<SingleSectionItems | SectionedItems>([]);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useClickAway(tooltipRef, () => {
        if (dropdownOpen) {
            setTimeout(() => {
                setDropdownOpen(false);
            }, 150);
        }
    });

    const handleSelectionChange = useCallback(
        (selectedKeys: string[]) => {
            onSelect(selectedKeys);
            setTimeout(() => {
                setDropdownOpen(false);
            }, 150);
        },
        [onSelect],
    );

    const allAudioDevicesDropdownItems = useMemo(
        () => ({
            inputs: getDropdownItemsForDevices({
                type: 'input',
                devices: inputDevices,
                activeDeviceId: activeInputDeviceId,
                t,
            }),
            outputs: getDropdownItemsForDevices({
                type: 'output',
                devices: outputDevices,
                activeDeviceId: activeOutputDeviceId,
                t,
            }),
        }),
        [inputDevices, activeInputDeviceId, t, outputDevices, activeOutputDeviceId],
    );

    const hasNoOutputs = useMemo(() => !allAudioDevicesDropdownItems.outputs.length, [allAudioDevicesDropdownItems]);

    useEffect(() => {
        if (hasNoOutputs) {
            setDropdownItems(allAudioDevicesDropdownItems.inputs as SingleSectionItems);
        } else {
            const sections = [
                {
                    sectionLabel: t('input'),
                    items: allAudioDevicesDropdownItems.inputs,
                },
                {
                    sectionLabel: t('output'),
                    items: allAudioDevicesDropdownItems.outputs,
                },
            ] as SectionedItems;
            setDropdownItems(sections);
        }
    }, [allAudioDevicesDropdownItems, hasNoOutputs, t]);

    const sharedDropdownProps = useMemo(
        () => ({
            selectionMode: 'single' as const,
            onSelectionChange: handleSelectionChange,
            placement: 'top' as const,
            isOpen: dropdownOpen,
        }),
        [handleSelectionChange, dropdownOpen],
    );

    const dropdownChild = useMemo(
        () => (
            <div>
                <AudioControlBarButton
                    onPress={() => setDropdownOpen(prev => !prev)}
                    icon={<FontAwesomeIcon icon={faGear} className="text-2xl sm:text-md" />}
                    label={hasNoOutputs ? t('microphoneSelect') : t('audioSelect')}
                />
            </div>
        ),
        [hasNoOutputs, t, setDropdownOpen],
    );

    if ((inputDevices.length < 2 && outputDevices.length < 2) || !dropdownItems.length) return null;

    return (
        <Tooltip
            content={hasNoOutputs ? t('microphoneSelect') : t('audioSelect')}
            classNames={{ base: dropdownOpen ? 'opacity-0' : undefined }}
            placement="top"
        >
            <div ref={tooltipRef}>
                {hasNoOutputs ? (
                    <Dropdown
                        type="single-section"
                        items={dropdownItems as SingleSectionItems}
                        selectedKeys={[getInputDeviceKeyForDeviceId(activeInputDeviceId)]}
                        {...sharedDropdownProps}
                    >
                        {dropdownChild}
                    </Dropdown>
                ) : (
                    <Dropdown
                        type="multi-section"
                        items={dropdownItems as SectionedItems}
                        selectedKeys={[
                            getInputDeviceKeyForDeviceId(activeInputDeviceId),
                            getOutputDeviceKeyForDeviceId(activeOutputDeviceId),
                        ]}
                        {...sharedDropdownProps}
                    >
                        {dropdownChild}
                    </Dropdown>
                )}
            </div>
        </Tooltip>
    );
}
